import { getApiBaseUrl } from '@/core/utils/apiUtils';
import { createAnalytics } from '@/shared/features/analytics/config/analytics.build';
import { SHOPFLO_PROVIDER_ID, SHOPFLO_SCRIPT_SRC } from '../../constants/index';
import type { SoftLoginProviderAdapter } from '../../domain/interfaces/SoftLoginProviderAdapter';

declare global {
  interface Window {
    Shopflo?: { m_id: string; [key: string]: unknown };
    handleFloGetUser?: () => Promise<string | null | undefined>;
  }
}

let shopfloStarted = false;

async function addSoftLoginContext(type: string, token: string, onSuccess: () => void): Promise<void> {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    console.error(`${type} soft login context error: missing API base URL`);
    return;
  }

  const url = new URL(`${baseUrl.replace(/\/+$/, '')}/user-context/add-sl`);
  url.searchParams.set('type', type);
  url.searchParams.set('t', token);

  try {
    const response = await fetch(url.toString(), { method: 'GET', credentials: 'include' });
    if (!response.ok) {
      console.error(`${type} soft login context error:`, response.status, response.statusText);
      return;
    }

    const data = (await response.json()) as { id?: string | number; type?: string };
    if (data?.id != null) {
      void createAnalytics()
        .then((analytics) => {
          analytics.loginUser({ userCode: String(data.id) });
          analytics.trackEvent('Soft Login', {
            'Soft Login Type': data.type ?? type,
          });
        })
        .catch(() => undefined);
    }

    onSuccess();
  } catch (error) {
    console.error(`${type} soft login context error:`, error);
  }
}

function pollShopfloUid(onUid: (uid: string) => void): void {
  const interval = window.setInterval(() => {
    if (typeof window.handleFloGetUser !== 'function') {
      return;
    }

    window.clearInterval(interval);
    void window
      .handleFloGetUser()
      .then((uid) => {
        if (uid) {
          onUid(uid);
        }
      })
      .catch((err) => console.error('Shopflo error:', err));
  }, 2000);

  window.setTimeout(() => window.clearInterval(interval), 15000);
}

function loadShopfloScript(onReady: () => void): void {
  const existing = document.querySelector(`script[src="${SHOPFLO_SCRIPT_SRC}"]`);
  if (existing) {
    onReady();
    return;
  }

  const merchantId = import.meta.env.VITE_SHOPFLO_MERCHANT_ID || '';
  window.Shopflo = window.Shopflo || { m_id: merchantId };

  const script = document.createElement('script');
  script.async = true;
  script.src = SHOPFLO_SCRIPT_SRC;
  script.onload = onReady;
  document.getElementsByTagName('script')[0]?.parentNode?.insertBefore(
    script,
    document.getElementsByTagName('script')[0],
  );
}

export class ShopfloSoftLoginAdapter implements SoftLoginProviderAdapter {
  readonly id = SHOPFLO_PROVIDER_ID;

  isEnabled(): boolean {
    return Boolean(import.meta.env.VITE_SHOPFLO_MERCHANT_ID);
  }

  start(onSuccess: () => void): void {
    if (shopfloStarted || typeof window === 'undefined' || !this.isEnabled()) {
      return;
    }

    shopfloStarted = true;
    loadShopfloScript(() => {
      pollShopfloUid((uid) => {
        void addSoftLoginContext(this.id, uid, onSuccess);
      });
    });
  }
}
